/**
 * Firebase web-push public configuration.
 *
 * These are the **public** Firebase web client values (safe to ship to the
 * browser). They are read from `VITE_`-prefixed env vars so Vite inlines them
 * into the client bundle. NEVER put the service-account JSON or any private key
 * here — those stay on the backend (`FCM_SERVICE_ACCOUNT_PATH`).
 *
 * When any value is missing, `isWebPushConfigured()` returns false and the
 * web-push path stays inert — mirroring the backend's graceful-skip behaviour
 * when `FCMProvider.health_check()` is false.
 */

export interface FirebaseWebConfig {
  apiKey: string;
  projectId: string;
  messagingSenderId: string;
  appId: string;
  /** GA4 measurement id — required for Firebase Analytics (getAnalytics). */
  measurementId: string;
}

const env = import.meta.env;

export const firebaseWebConfig: FirebaseWebConfig = {
  apiKey: env.VITE_FIREBASE_API_KEY ?? '',
  projectId: env.VITE_FIREBASE_PROJECT_ID ?? '',
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? '',
  appId: env.VITE_FIREBASE_APP_ID ?? '',
  measurementId: env.VITE_FIREBASE_MEASUREMENT_ID ?? '',
};

/** Public VAPID key from Firebase Console → Cloud Messaging → Web Push certificates. */
export const webVapidKey: string = env.VITE_FCM_VAPID_KEY ?? '';

/** True only when every value needed to acquire a web push token is present. */
export function isWebPushConfigured(): boolean {
  return Boolean(
    firebaseWebConfig.apiKey &&
      firebaseWebConfig.projectId &&
      firebaseWebConfig.messagingSenderId &&
      firebaseWebConfig.appId &&
      webVapidKey,
  );
}

/** True only when every value needed for Firebase Analytics (GA4) is present. */
export function isAnalyticsConfigured(): boolean {
  return Boolean(
    firebaseWebConfig.apiKey &&
      firebaseWebConfig.projectId &&
      firebaseWebConfig.appId &&
      firebaseWebConfig.measurementId,
  );
}
